import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Search, ChevronDown, Brain, CreditCard,
  Leaf, Star, HelpCircle, Sparkles, ArrowRight,
  X, MessageCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SEOMeta } from '@/components/SEOMeta';
import { PageBreadcrumb } from '@/components/PageBreadcrumb';
import { useTheme } from '@/contexts/ThemeContext';

const BREADCRUMBS = [{ label: 'Въпроси и отговори', href: '/faq' }];

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { q: 'Как изкуственият интелект генерира хранителния ми план?', a: 'portion.bg използва Gemini — Google\'s напреднал AI модел. След като въведеш данните си (тегло, ръст, цел, активност, бюджет), AI анализира комбинацията и генерира уникален 7-дневен план с балансирани ястия, точни макронутриенти и реални рецепти. Всеки план е различен — дори при едни и същи входни данни.' },
    { q: 'По-добър ли е AI планът от ръчно съставените диети?', a: 'AI моделът обработва хиляди варианти за секунди и намира оптималната комбинация от храни, която отговаря ТОЧНО на твоите калорийни нужди, бюджет и предпочитания. Ръчно съставената диета изисква часове работа на нутриционист и пак може да не е оптимизирана за твоите конкретни параметри.' },
    { q: 'Кой AI модел използвате?', a: 'Използваме Gemini Flash — Google\'s водещ AI модел за текстова генерация, специализиран за структурирано и точно съдържание, идеален за генериране на хранителни планове с конкретни калорийни цели и макронутриентни разпределения.' },
    { q: 'Адаптира ли се AI планът при промяна на целите ми?', a: 'Да. Всеки път, когато генерираш нов план, можеш да промениш целта (отслабване/поддържане/качване), активността и бюджета. AI генерира изцяло нов персонализиран план на база актуалните параметри.' },
    { q: 'Какво получавам с абонамента?', a: 'Пълен 7-дневен AI-генериран персонализиран план, автоматичен списък за пазаруване в 3 бюджетни варианта, PDF изтегляне на плана, неограничено генериране на нови планове и приоритетна поддръжка.' },
    { q: 'Мога ли да отменя абонамента?', a: 'Да, по всяко време без неустойки. Достъпът остава активен до края на платения период. Без минимален срок, без скрити такси — пълна свобода.' },
    { q: 'Колко често мога да генерирам нов план?', a: 'Неограничено. Като абонат можеш да генерираш нов план по всяко време — при промяна на цел, тегло, сезон или просто когато искаш разнообразие.' },
    { q: 'Какви начини на плащане приемате?', a: 'Приемаме всички основни кредитни и дебитни карти (Visa, MasterCard, American Express) чрез Stripe — световен лидер в онлайн плащанията.' },
    { q: 'Какво включва хранителният план?', a: '7-дневен меню с 4 хранения на ден (закуска, обяд, вечеря, снак), пълни рецепти с начин на приготвяне, точни макронутриенти (белтъци, въглехидрати, мазнини) за всяко ястие, седмичен списък за пазаруване и бюджетна оценка.' },
    { q: 'Как се изчисляват калориите и макросите?', a: 'Използваме формулата Mifflin-St Jeor за изчисляване на базовия метаболизъм (BMR), умножен по коефициент за активност (1.2–1.9) за TDEE. Макронутриентите се разпределят по системата Atwater: 4 ккал/г белтъци и въглехидрати, 9 ккал/г мазнини.' },
    { q: 'Планът съобразен ли е с моята диета?', a: 'Да. Поддържаме традиционна и веганска диета. Можеш да изключиш конкретни храни (алергии, непоносимости) при генерирането на плана. Ако имаш специфичен режим (кето, периодично гладуване), маркирай го в настройките.' },
    { q: 'Може ли планът да включва интермитентното гладуване?', a: 'Да. По време на квиза можеш да маркираш опция за интермитентно гладуване (16:8). AI ще адаптира хранителния прозорец и ще разпредели калориите в рамките на 8-часовото хранене.' },
    { q: 'Планът съобразен ли е с бюджета ми?', a: 'Да. Избираш бюджетно ниво: Нисък (икономично), Стандарт (баланс) или Премиум (висококачествени продукти). AI генерира меню с продукти, достъпни в съответното ценово ниво в обичайния супермаркет.' },
    { q: 'Какво отличава portion.bg от другите подобни платформи?', a: 'Три ключови разлики: 1) Реален AI (Gemini) генерира уникален план за теб. 2) Адаптиран за България — продукти от местния пазар, цени в лева/евро, рецепти с местна кухня. 3) Прозрачен модел — Ден 1 е напълно безплатен, без скрити такси.' },
    { q: 'Безплатен ли е portion.bg?', a: 'Калкулаторът и Ден 1 от плана са напълно безплатни — без карта, без регистрация. За пълния 7-дневен план с всички функции се изисква абонамент от €7.99/месец.' },
    { q: 'Трябва ли да се регистрирам?', a: 'За калкулатора — не. За получаване и запазване на плана — да, трябва да създадеш акаунт. Регистрацията отнема под 30 секунди.' },
    { q: 'Сигурни ли са личните ми данни?', a: 'Да. Не съхраняваме медицински данни. Паролите са хеширани с bcrypt. Плащанията минават изцяло през Stripe. Спазваме GDPR и имаш пълно право на изтриване на данните.' },
    { q: 'Има ли медицински отказ?', a: 'Да. portion.bg е образователна платформа и не замества медицинска консултация. Не е подходяща за лица с диабет, инсулинова резистентност, Хашимото или други метаболитни нарушения. Консултирай се с лекар преди промяна в хранителния режим.' },
  ].map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const CATEGORIES = [
  { id: 'all',          label: 'Всички',        icon: HelpCircle },
  { id: 'ai',           label: 'Изкуствен интелект', icon: Brain },
  { id: 'subscription', label: 'Абонамент',     icon: CreditCard },
  { id: 'plans',        label: 'Планове',        icon: Leaf },
  { id: 'platform',     label: 'Платформа',      icon: Star },
  { id: 'general',      label: 'Общи',           icon: HelpCircle },
];

const ALL_FAQS = [
  /* ── AI ── */
  {
    category: 'ai',
    q: 'Как изкуственият интелект генерира хранителния ми план?',
    a: 'portion.bg използва Gemini — Google\'s напреднал AI модел. След като въведеш данните си (тегло, ръст, цел, активност, бюджет), AI анализира комбинацията и генерира уникален 7-дневен план с балансирани ястия, точни макронутриенти и реални рецепти. Всеки план е различен — дори при едни и същи входни данни.',
  },
  {
    category: 'ai',
    q: 'По-добър ли е AI планът от ръчно съставените диети?',
    a: 'AI моделът обработва хиляди варианти за секунди и намира оптималната комбинация от храни, която отговаря ТОЧНО на твоите калорийни нужди, бюджет и предпочитания. Ръчно съставената диета изисква часове работа на нутриционист и пак може да не е оптимизирана за твоите конкретни параметри.',
  },
  {
    category: 'ai',
    q: 'Адаптира ли се AI планът при промяна на целите ми?',
    a: 'Да. Всеки път, когато генерираш нов план (с абонамент — неограничено), можеш да промениш целта (отслабване/поддържане/качване), активността и бюджета. AI генерира изцяло нов персонализиран план на база актуалните параметри.',
  },
  {
    category: 'ai',
    q: 'Кой AI модел използвате?',
    a: 'Използваме Gemini Flash — Google\'s водещ AI модел за текстова генерация. Той е специализиран за структурирано и точно съдържание, идеален за генериране на хранителни планове с конкретни калорийни цели и макронутриентни разпределения.',
  },

  /* ── Subscription ── */
  {
    category: 'subscription',
    q: 'Какво получавам с абонамента?',
    a: 'Пълен 7-дневен AI-генериран персонализиран план (не само Ден 1), автоматичен списък за пазаруване в 3 бюджетни варианта, PDF изтегляне на плана, неограничено генериране на нови планове и приоритетна поддръжка.',
  },
  {
    category: 'subscription',
    q: 'Мога ли да отменя абонамента?',
    a: 'Да, по всяко време без неустойки. Достъпът остава активен до края на платения период. Без минимален срок, без скрити такси — пълна свобода.',
  },
  {
    category: 'subscription',
    q: 'Колко често мога да генерирам нов план?',
    a: 'Неограничено. Като абонат можеш да генерираш нов план по всяко време — при промяна на цел, тегло, сезон или просто когато искаш разнообразие.',
  },
  {
    category: 'subscription',
    q: 'Как работят промо кодовете?',
    a: 'При страницата за плащане (/checkout) има поле за промо код. Въведи кода и системата автоматично изчислява отстъпката преди плащане. Промо кодовете са с ограничен срок и еднократна употреба.',
  },
  {
    category: 'subscription',
    q: 'Какви начини на плащане приемате?',
    a: 'Приемаме всички основни кредитни и дебитни карти (Visa, MasterCard, American Express) чрез Stripe — световен лидер в онлайн плащанията с военно ниво на защита.',
  },

  /* ── Plans ── */
  {
    category: 'plans',
    q: 'Какво включва хранителният план?',
    a: '7-дневен меню с 4 хранения на ден (закуска, обяд, вечеря, снак), пълни рецепти с начин на приготвяне, точни макронутриенти (белтъци, въглехидрати, мазнини) за всяко ястие, седмичен списък за пазаруване и бюджетна оценка в 3 варианта.',
  },
  {
    category: 'plans',
    q: 'Как се изчисляват калориите и макросите?',
    a: 'Използваме формулата Mifflin-St Jeor за изчисляване на базовия метаболизъм (BMR), умножен по коефициент за активност (1.2–1.9) за TDEE. Макронутриентите се разпределят по системата Atwater: 4 ккал/г белтъци и въглехидрати, 9 ккал/г мазнини.',
  },
  {
    category: 'plans',
    q: 'Планът съобразен ли е с моята диета?',
    a: 'Да. Поддържаме традиционна и веганска диета. Можеш да изключиш конкретни храни (алергии, непоносимости, нехаресвани продукти) при генерирането на плана. Ако имаш специфичен режим (кето, периодично гладуване), маркирай го в настройките.',
  },
  {
    category: 'plans',
    q: 'Може ли планът да включва постенето (интермитентно гладуване)?',
    a: 'Да. По време на квиза можеш да маркираш опция за интермитентно гладуване (16:8). AI ще адаптира хранителния прозорец и ще разпредели калориите в рамките на 8-часовото хранене.',
  },
  {
    category: 'plans',
    q: 'Планът съобразен ли е с бюджета ми?',
    a: 'Да. Избираш бюджетно ниво: Нисък (икономично), Стандарт (баланс) или Премиум (висококачествени продукти). AI генерира меню с продукти, достъпни в съответното ценово ниво в обичайния супермаркет.',
  },
  {
    category: 'plans',
    q: 'Има ли медицински отказ?',
    a: 'Да. portion.bg е образователна платформа и не замества медицинска консултация. Не е подходяща за лица с диабет, инсулинова резистентност, Хашимото или други метаболитни нарушения. Консултирай се с лекар преди промяна в хранителния режим.',
  },

  /* ── Platform / Differentiation ── */
  {
    category: 'platform',
    q: 'Какво отличава portion.bg от другите подобни платформи?',
    a: 'Три ключови разлики: 1) Реален AI (Gemini) генерира уникален план за теб — не пресмята от готова база от шаблони. 2) Адаптиран за България — продукти от местния пазар, цени в лева/евро, рецепти с местна кухня. 3) Прозрачен модел — Ден 1 е напълно безплатен, без скрити такси.',
  },
  {
    category: 'platform',
    q: 'Защо да избера portion.bg пред готовите диети в интернет?',
    a: 'Готовите диети са общи — не знаят твоето тегло, ръст, активност, цел или бюджет. portion.bg изчислява точно твоите дневни нужди и генерира план САМО за теб, с продукти, които можеш да намериш в близкия магазин на достъпна цена.',
  },
  {
    category: 'platform',
    q: 'Работи ли планът за вегани и вегетарианци?',
    a: 'Да. При попълването на квиза избираш „Веган" или „Вегетарианец" и AI автоматично изключва месо/животински продукти от плана, заменяйки ги с растителни източници на белтъци (бобови, тофу, темпе, ядки).',
  },
  {
    category: 'platform',
    q: 'Сигурни ли са личните ми данни?',
    a: 'Да. Не съхраняваме медицински данни. Паролите са хеширани с bcrypt. Плащанията минават изцяло през Stripe — ние никога не виждаме данните на картата ти. Спазваме GDPR и имаш пълно право на изтриване на данните.',
  },

  /* ── General ── */
  {
    category: 'general',
    q: 'Безплатен ли е portion.bg?',
    a: 'Калкулаторът и Ден 1 от плана са напълно безплатни — без карта, без регистрация. За пълния 7-дневен план с всички функции се изисква абонамент от €7.99/месец.',
  },
  {
    category: 'general',
    q: 'Трябва ли да се регистрирам?',
    a: 'За калкулатора — не. За получаване и запазване на плана — да, трябва да създадеш акаунт. Регистрацията отнема под 30 секунди.',
  },
  {
    category: 'general',
    q: 'Как мога да свържа с екипа?',
    a: 'Пиши ни на info@portion.bg или чрез формата за контакт на /contact. Отговаряме в рамките на 24 часа в работни дни.',
  },
];

export default function FAQPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [openIdx, setOpenIdx] = useState(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return ALL_FAQS.filter(faq => {
      const matchCat = category === 'all' || faq.category === category;
      const matchSearch = !term ||
        faq.q.toLowerCase().includes(term) ||
        faq.a.toLowerCase().includes(term);
      return matchCat && matchSearch;
    });
  }, [search, category]);

  const handleCategoryChange = (id) => {
    setCategory(id);
    setOpenIdx(null);
  };

  return (
    <>
      <SEOMeta
        title="Въпроси и отговори"
        description="Намери отговори на честите въпроси за portion.bg — как работи AI алгоритъмът, абонамент, хранителни планове и какво отличава платформата от конкурентите."
        keywords="portion.bg въпроси, AI хранителен план FAQ, TDEE калкулатор, абонамент, диета въпроси"
        schemas={[FAQ_SCHEMA]}
        canonical="/faq"
        breadcrumbs={BREADCRUMBS}
      />
      <PageBreadcrumb items={BREADCRUMBS} />

      {/* ── Hero ── */}
      <div className="bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5"
              style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.25)' }}>
              <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-400 tracking-wide">ЗАХРАНЕНА С ИЗКУСТВЕН ИНТЕЛЕКТ</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              Въпроси и отговори
            </h1>
            <p className="text-gray-400 text-base sm:text-lg mb-10 max-w-xl mx-auto">
              Всичко, което трябва да знаеш за portion.bg и AI-генерираните хранителни планове
            </p>

            {/* Search bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setOpenIdx(null); }}
                placeholder="Търси въпрос..."
                data-testid="faq-search-input"
                className="w-full pl-12 pr-12 py-3.5 rounded-2xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
              />
              {search && (
                <button onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* ── Category Filters ── */}
        <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="faq-category-filters">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const active = category === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                data-testid={`faq-filter-${cat.id}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-150"
                style={{
                  background: active
                    ? (isDark ? 'rgba(255,255,255,0.12)' : '#111827')
                    : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                  color: active ? 'white' : (isDark ? 'rgba(255,255,255,0.55)' : '#6B7280'),
                  border: active ? '1px solid ' + (isDark ? 'rgba(255,255,255,0.15)' : '#111827') : '1px solid transparent',
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat.label}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-gray-400">
            {filtered.length} {filtered.length === 1 ? 'въпрос' : 'въпроса'}
          </span>
        </div>

        {/* ── FAQ List ── */}
        {filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-16">
            <HelpCircle className="w-12 h-12 text-gray-200 dark:text-slate-700 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">Няма намерени въпроси</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Опитай с различен термин или категория</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((faq, i) => {
              const isOpen = openIdx === i;
              const catDef = CATEGORIES.find(c => c.id === faq.category);
              return (
                <motion.div
                  key={`${faq.category}-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.22 }}
                >
                  <div
                    className="rounded-2xl overflow-hidden transition-all duration-200"
                    style={{
                      background: isOpen ? '#0D0F0E' : (isDark ? '#1e293b' : 'white'),
                      border: isOpen ? '1px solid rgba(5,150,105,0.25)' : (isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)'),
                      boxShadow: isOpen
                        ? '0 4px 20px rgba(0,0,0,0.12)'
                        : (isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)'),
                    }}
                  >
                    <button
                      onClick={() => setOpenIdx(isOpen ? null : i)}
                      data-testid={`faq-item-${i}`}
                      className="w-full text-left px-5 py-4 flex items-start gap-3"
                    >
                      <div className={`flex-1 min-w-0`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          {catDef && catDef.id !== 'all' && (
                            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                              isOpen
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : (isDark ? 'bg-slate-700 text-gray-300' : 'bg-gray-100 text-gray-500')
                            }`}>
                              {catDef.label}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm font-semibold leading-snug transition-colors ${
                          isOpen ? 'text-white' : (isDark ? 'text-gray-200' : 'text-gray-900')
                        }`}>
                          {faq.q}
                        </p>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 transition-all duration-200 ${
                          isOpen ? 'rotate-180 text-emerald-400' : 'text-gray-400'
                        }`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="answer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22, ease: 'easeOut' }}
                          style={{ borderTop: '1px solid rgba(5,150,105,0.12)' }}
                        >
                          <p className="px-5 py-4 text-sm text-white/60 leading-relaxed">
                            {faq.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* ── Bottom CTA ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mt-16 bg-gray-900 rounded-3xl p-8 sm:p-12 text-center"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5"
            style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.25)' }}>
            <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-400">НЕ НАМЕРИ ОТГОВОР?</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
            Свържи се с нас
          </h2>
          <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
            Екипът ни отговаря в рамките на 24 часа в работни дни. Питай смело.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white transition-all"
                style={{ background: '#059669', boxShadow: '0 0 20px rgba(5,150,105,0.25)' }}>
                <MessageCircle className="w-4 h-4" />
                Изпрати съобщение
              </button>
            </Link>
            <Link to="/calculator">
              <button className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium text-white/60 hover:text-white border border-white/15 hover:border-white/30 transition-all">
                Изчисли плана си <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </>
  );
}
